/**
 * Card — a resting surface with an optional title / subtitle / action header.
 *
 * `padded` controls the body padding; set it false when the body is a table
 * or list that manages its own padding.
 */

import type { ReactNode } from 'react';
import { cx } from './cx';

interface CardProps {
  children: ReactNode;
  className?: string;
  /** Pad the card body. Default true; false for edge-to-edge tables/lists. */
  padded?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned header slot — buttons, links, menus. */
  action?: ReactNode;
}

export default function Card({
  children,
  className = '',
  padded = true,
  title,
  subtitle,
  action,
}: CardProps) {
  return (
    <div
      className={cx('bg-surface border border-outline-variant rounded-xl shadow-card', className)}
    >
      {(title || action) && (
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-outline-variant">
          <div>
            {title && <h3 className="text-sm font-semibold text-on-surface">{title}</h3>}
            {subtitle && <p className="text-xs text-outline mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </div>
  );
}
